import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

import { GIButton } from 'app-components';

import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import { useAppSelector, useAppDispatch } from '@hooks/index';
import { historicalMigrationSelector } from '@selectors/applicationsSelectors';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { createAccountMigrationTicket } from '@actions/individualPortal/applications/applicationsActionCreators';

import { ConditionalRendering } from '@components/common/conditional-rendering';
import { ALPHA_NUMERIC_SPECIAL_CHARS_REGEX } from '@utils/validators/regex';
import { configs } from '@constants/configs/configs';

const REFERENCE_ID_FORMAT_ERROR_ID = 'historicalApp.migration.referenceIdFormatError';
const SUBMIT_ERROR_ID = 'historicalApp.migration.submitError';
const REFERENCE_ID_MAX_LENGTH = 20;

const StyledAlertMessage = styled(AlertMessage)({
    '& .gi-alert': {
        alignItems: 'flex-start',
    },
    '& .gi-alert__icon': {
        paddingTop: '0.35em',
    },
});

const HistoricalAccountMigrationSection = () => {
    const intl = useIntl();
    const historicalMigration = useAppSelector(historicalMigrationSelector);
    const [showLinkCard, setShowLinkCard] = useState<boolean>(false);
    const [referenceId, setReferenceId] = useState<string>(historicalMigration?.referenceId ?? '');
    const [formErrorId, setFormErrorId] = useState<string | null>(null);

    const dispatch = useAppDispatch();

    const openLinkCard = () => {
        setShowLinkCard(true);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const trimmed = referenceId.trim();
        if (!trimmed) {
            setFormErrorId(REFERENCE_ID_FORMAT_ERROR_ID);
            return;
        }

        if (!ALPHA_NUMERIC_SPECIAL_CHARS_REGEX.test(trimmed)) {
            setFormErrorId(REFERENCE_ID_FORMAT_ERROR_ID);
            return;
        }

        setFormErrorId(null);
        const result = await dispatch(createAccountMigrationTicket(trimmed));
        if (!result?.ticketNumber) {
            setFormErrorId(SUBMIT_ERROR_ID);
        }
    };

    const handleReferenceIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (!val || ALPHA_NUMERIC_SPECIAL_CHARS_REGEX.test(val)) {
            setFormErrorId(null);
        } else {
            setFormErrorId(REFERENCE_ID_FORMAT_ERROR_ID);
        }
        setReferenceId(val);
    };

    const supportLink = (
        <Link
            to={configs.routes.TICKET_CONSUMER}
            className="historical-migration-link__unstyled-button historical-migration-link__button-link"
            data-testid="navigate-to-tickets"
        >
            <FormattedMessage id="indportal.sidenav.dashboard.tickets" />
        </Link>
    );

    const ticketDetailsLink = (
        <Link
            to={historicalMigration?.encryptedTicketId ?
                configs.routes.TICKET_CONSUMER_DETAIL.replace(':id', historicalMigration?.encryptedTicketId) :
                configs.routes.TICKET_CONSUMER}
            className="historical-migration-link__unstyled-button historical-migration-link__button-link"
            data-testid="navigate-to-ticket-details"
        >
            {historicalMigration?.ticketId}
        </Link>
    );

    return (
        <>
            <ConditionalRendering isTrue={!!historicalMigration?.ticketId}>
                <StyledAlertMessage
                    showIcon
                    icon="check-circle"
                    type="success"
                    content={
                        <div>
                            <span className="historical-migration-link__semibold-text">
                                <FormattedMessage
                                    id={
                                        historicalMigration?.referenceId
                                            ? 'historicalApp.migration.successTitle'
                                            : 'historicalApp.migration.successTitleRevisit'
                                    }
                                    values={{ ticketDetailsLink }}
                                />
                            </span>
                            <br />
                            <FormattedMessage id="historicalApp.migration.successBody" />
                            <br />
                            <FormattedMessage
                                id="historicalApp.migration.successSupportLink"
                                values={{
                                    supportLink,
                                }}
                            />
                        </div>
                    }
                />
            </ConditionalRendering>
            <ConditionalRendering isTrue={!historicalMigration?.ticketId}>
                <StyledAlertMessage
                    showIcon
                    icon="exclamation-triangle"
                    type="warning"
                    content={
                        <div>
                            <p className="margin-bottom-1">
                                <FormattedMessage id="historicalApp.alert" values={{ br: <br /> }} />
                            </p>
                            <p className="margin-bottom-0">
                                <FormattedMessage
                                    id="historicalApp.linkAccount"
                                    values={{
                                        clickHere: (
                                            <button
                                                type="button"
                                                onClick={openLinkCard}
                                                className="historical-migration-link__unstyled-button historical-migration-link__button-link"
                                            >
                                                <FormattedMessage id="common.clickHereStandalone" />
                                            </button>
                                        ),
                                    }}
                                />
                            </p>
                        </div>
                    }
                />
            </ConditionalRendering>
            <ConditionalRendering isTrue={showLinkCard}>
                <div className="usa-card">
                    <div className="gi-panel" data-testid="historical-migration-link-card">
                        <div className="gi-panel__header historical-migration-link__card-header">
                            <h5
                                className="gi-panel__heading historical-migration-link__card-heading margin-bottom-2"
                                id="migration-card-title"
                            >
                                <FormattedMessage id="historicalApp.migration.cardTitle" />
                            </h5>
                            <ConditionalRendering isTrue={!historicalMigration?.ticketId}>
                                <button
                                    type="button"
                                    className="historical-migration-link__unstyled-button"
                                    onClick={() => {
                                        setShowLinkCard(false);
                                        setFormErrorId(null);
                                    }}
                                    aria-label={intl.formatMessage({ id: 'button.close' })}
                                >
                                    <FontAwesomeIcon icon="times" aria-hidden />
                                </button>
                            </ConditionalRendering>
                        </div>
                        <div className="gi-panel__body">
                            <p className="margin-bottom-1">
                                <FormattedMessage id="historicalApp.migration.referenceIdHintIntro" />
                            </p>
                            <ul className="historical-migration-link__hints">
                                <li>
                                    <FormattedMessage id="historicalApp.migration.referenceIdHint1" />
                                </li>
                                <li>
                                    <FormattedMessage id="historicalApp.migration.referenceIdHint2" />
                                </li>
                                <li>
                                    <FormattedMessage id="historicalApp.migration.referenceIdHint3" />
                                </li>
                            </ul>
                            <hr />
                            <form
                                data-testid="historical-migration-form"
                                onSubmit={handleSubmit}
                                aria-labelledby="migration-card-title"
                            >
                                <label htmlFor="referenceId">
                                    <FormattedMessage id="historicalApp.migration.referenceIdLabel" />
                                </label>
                                <br />
                                <input
                                    id="referenceId"
                                    className={`tablet:grid-col-6 gi-${INPUT_TYPES.REGULAR_INPUT}__input historical-migration-link__reference-id-input`}
                                    type={INPUT_TYPES.REGULAR_INPUT}
                                    value={referenceId}
                                    aria-invalid={!!formErrorId}
                                    aria-describedby={formErrorId ? 'historical-migration-error' : undefined}
                                    onChange={handleReferenceIdChange}
                                    placeholder={intl.formatMessage({
                                        id: 'historicalApp.migration.referenceIdPlaceholder',
                                    })}
                                    autoComplete="off"
                                    disabled={!!historicalMigration?.ticketId}
                                    maxLength={REFERENCE_ID_MAX_LENGTH}
                                />

                                <GIButton
                                    id="historical-migration-submit-button"
                                    type="submit"
                                    labelId="button.submit"
                                    variant="primary"
                                    isDisabled={
                                        !ALPHA_NUMERIC_SPECIAL_CHARS_REGEX.test(referenceId) ||
                                        Boolean(historicalMigration?.ticketId)
                                    }
                                />
                                <div
                                    id="historical-migration-error"
                                    data-testid="historical-migration-submit-error"
                                    role="alert"
                                >
                                    <ConditionalRendering isTrue={Boolean(formErrorId)}>
                                        <AlertMessage
                                            type="error"
                                            content={
                                                <p data-testid="historical-migration-submit-error-message">
                                                    <FormattedMessage id={formErrorId} />
                                                </p>
                                            }
                                        />
                                    </ConditionalRendering>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </ConditionalRendering>
        </>
    );
};

export default HistoricalAccountMigrationSection;
